import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";

import { SliceKeys, tablePaginationInitialValue } from "@sso/constant";
import { RootState } from "@sso/store";
import { SearchColumn } from "@sso/types";
import { getTableSortColumn, queryBuilder } from "@sso/utils";

export const APIAccountSlice = createSlice({
  name: SliceKeys.API_Account,
  initialState: { openApiKeysTable: false, pagination: tablePaginationInitialValue, apiAccountId: "" },
  reducers: {
    setAPIAccountPageSize: (state, action: PayloadAction<number>) => {
      state.pagination.pageIndex = tablePaginationInitialValue.pageIndex;
      state.pagination.pageSize = action.payload;
    },
    setAPIAccountPageIndex: (state, action: PayloadAction<number>) => {
      state.pagination.pageIndex = action.payload;
    },
    setAPIAccountSearchColumn: (state, action: PayloadAction<SearchColumn>) => {
      state.pagination.pageIndex = tablePaginationInitialValue.pageIndex;
      state.pagination.search[action.payload.column] = action.payload;
    },
    clearAPIAccountSearchColumn: (state, action: PayloadAction<string>) => {
      const { [action.payload]: deleteItem, ...otherSearchItems } = state.pagination.search;
      state.pagination.search = otherSearchItems;
    },
    setAPIAccountSortColumn: (state, action: PayloadAction<string>) => {
      const { column, type } = state.pagination.sort;
      state.pagination.sort = getTableSortColumn(column, action.payload, type);
      state.pagination.pageIndex = tablePaginationInitialValue.pageIndex;
    },
    openApiKeysTable: (state, action: PayloadAction<string>) => {
      state.openApiKeysTable = true;
      state.apiAccountId = action.payload;
    },
    closeApiKeysTable: state => {
      state.openApiKeysTable = false;
    },
  },
});

export const {
  setAPIAccountPageSize,
  setAPIAccountPageIndex,
  setAPIAccountSortColumn,
  setAPIAccountSearchColumn,
  clearAPIAccountSearchColumn,
  openApiKeysTable,
  closeApiKeysTable,
} = APIAccountSlice.actions;

export const selectAPIAccountPagination = createSelector(
  (state: RootState) => state.apiAccount.pagination,
  ({ pageSize, totalPages, pageIndex, sort, search }) => ({
    pageSize: { action: setAPIAccountPageSize, value: pageSize },
    pagination: {
      totalPages,
      pageIndex,
      action: setAPIAccountPageIndex,
    },
    sort,
    search: { object: search, onChange: setAPIAccountSearchColumn, onClear: clearAPIAccountSearchColumn },
  }),
);
export const selectAPIAccountQuery = createSelector(
  (state: RootState) => state.apiAccount.pagination,
  ({ pageSize, pageIndex, search, sort }) => queryBuilder(pageSize, pageIndex, search, sort),
);

export const selectApiKeysTableStatus = createSelector(
  (state: RootState) => state.apiAccount.openApiKeysTable,
  openApiKeysTable => openApiKeysTable,
);

export const selectApiKeysAccountId = createSelector(
  (state: RootState) => state.apiAccount.apiAccountId,
  apiAccountId => apiAccountId,
);

export default APIAccountSlice.reducer;
